import React, { useState } from 'react'
import { useCurrentUser } from '@/hooks/useCurrentUser'
import { useProduct } from '@/hooks/api/useProducts'
import { Search, Lock } from 'lucide-react'
import { useQueryClient } from '@tanstack/react-query'
import { useUserCollections } from '@/hooks/api/useUsers'
import { useCollectionToggle } from '@/hooks/useCollectionToggle'
import { SaveButton } from '@/components/shared/atoms/SaveButton'
import { CreateCollectionFlow } from '@/components/create/collection/CreateCollectionFlow'
import { CreateCollectionListItem } from '@/components/shared/collection-selector/CreateCollectionListItem'
import type { NormalizedCollection } from '@wtf/shared'
import { classNames } from '@/utils/classNames'
import { useProtectedAction } from '@/hooks/useProtectedAction'
import { transformSmall } from '@wtf/shared'

interface AddToCollectionPanelProps {
    productId: string
    rootClassName?: string
    open: boolean
    onClose: () => void
    size?: 'default' | 'large'
    onCreateFlowOpenChange?: (isOpen: boolean) => void
}

export const AddToCollectionPanel = React.forwardRef<
    HTMLDivElement,
    AddToCollectionPanelProps
>(
    (
        {
            productId,
            open,
            onClose,
            rootClassName,
            size = 'default',
            onCreateFlowOpenChange,
        },
        ref
    ) => {
        const { user } = useCurrentUser()
        const { data: productData } = useProduct(productId)
        const queryClient = useQueryClient()
        const { data: collectionIds = [], isLoading: collectionsLoading } =
            useUserCollections(user?.uid)
        const { handleToggle, isProductInCollection } =
            useCollectionToggle(productData)
        const { withAuth } = useProtectedAction()
        const [query, setQuery] = useState('')
        const [hoveredId, setHoveredId] = useState<string | null>(null)
        const [showCreateCollectionFlow, setShowCreateCollectionFlow] =
            useState(false)

        // Notify parent when create flow state changes
        React.useEffect(() => {
            onCreateFlowOpenChange?.(showCreateCollectionFlow)
        }, [showCreateCollectionFlow, onCreateFlowOpenChange])

        // Enhanced toggle function that closes panel after action
        const handleToggleWithClose = (collectionId: string) => {
            // Perform the toggle action (now automatically tracks via mutation hooks)
            handleToggle(collectionId)

            // Close panel after a brief delay to show the action feedback
            setTimeout(() => {
                onClose()
            }, 100)
        }

        const filteredIds = (collectionIds || []).filter((cid) => {
            const col = queryClient.getQueryData<NormalizedCollection>([
                'collection',
                cid,
            ])
            if (!col || col.isArchived) return false
            const name = col?.name?.toLowerCase() || ''
            return name.includes(query.toLowerCase())
        })

        return (
            <>
                {open && (
                    <div
                        ref={ref}
                        className={classNames(
                            rootClassName,
                            'absolute top-14 z-10 rounded-xl border-gray-100 bg-white py-2 shadow-lg',
                            size === 'large'
                                ? '-right-[30%] w-96'
                                : '-right-[10%] w-[120%] max-w-[400px]'
                        )}
                    >
                        <div className="">
                            <div className="px-3 pt-3 pb-2">
                                <p className="text-center text-sm font-semibold">
                                    Save to collection
                                </p>
                                <div className="mt-4">
                                    <div
                                        className="flex h-9 w-full items-center gap-3 rounded-xl border border-[#91918b] px-3 text-sm focus:ring-2 focus:ring-black/20 focus:outline-none"
                                        onClick={(e) => {
                                            e.preventDefault()
                                            e.stopPropagation()
                                        }}
                                    >
                                        <Search size={16} color="#91918b" />
                                        <input
                                            value={query}
                                            onChange={(e) =>
                                                setQuery(e.target.value)
                                            }
                                            onClick={(e) => {
                                                e.preventDefault()
                                                e.stopPropagation()
                                            }}
                                            placeholder="Search"
                                            className="w-full border-0 bg-white outline-none placeholder:text-xs focus:border-none"
                                        />
                                    </div>
                                </div>
                            </div>
                            {/* Create new collection button */}
                            <CreateCollectionListItem
                                onClick={(e) => {
                                    e.preventDefault()
                                    e.stopPropagation()
                                    withAuth(() =>
                                        setShowCreateCollectionFlow(true)
                                    )()
                                }}
                            />
                            <div className="max-h-80 space-y-1 overflow-y-auto px-2 pb-2">
                                {collectionsLoading && (
                                    <div className="px-2 py-2 text-center text-xs text-gray-500">
                                        Loading...
                                    </div>
                                )}
                                {!collectionsLoading &&
                                    filteredIds.length === 0 && (
                                        <div className="px-2 py-2 text-center text-xs text-gray-500">
                                            No collections
                                        </div>
                                    )}
                                {filteredIds.map((cid) => {
                                    const collection =
                                        queryClient.getQueryData<NormalizedCollection>(
                                            ['collection', cid]
                                        )
                                    if (!collection) return null

                                    const isSelected =
                                        isProductInCollection(cid)

                                    return (
                                        <div
                                            key={cid}
                                            className="group flex cursor-pointer items-center justify-between gap-3 rounded-xl px-2 py-2 hover:bg-gray-100"
                                            onMouseEnter={() =>
                                                setHoveredId(cid)
                                            }
                                            onMouseLeave={() =>
                                                setHoveredId(null)
                                            }
                                            onClick={(e) => {
                                                e.preventDefault()
                                                e.stopPropagation()
                                                handleToggleWithClose(cid)
                                            }}
                                        >
                                            <div className="flex min-w-0 items-center gap-3">
                                                <div className="h-8 w-10 shrink-0 overflow-hidden rounded-lg bg-gray-200">
                                                    {collection.coverImageUrl && (
                                                        <img
                                                            src={
                                                                transformSmall(
                                                                    collection.coverImageUrl
                                                                ) ||
                                                                collection.coverImageUrl
                                                            }
                                                            alt={
                                                                collection.name
                                                            }
                                                            className="h-full w-full object-cover"
                                                        />
                                                    )}
                                                </div>
                                                <div className="min-w-0">
                                                    <p className="truncate text-sm font-medium text-black">
                                                        {collection.name}
                                                    </p>
                                                    <p className="text-xs text-gray-500">
                                                        {
                                                            collection.totalProductCount
                                                        }{' '}
                                                        products
                                                    </p>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <SaveButton
                                                    isSaved={isSelected}
                                                    size="sm"
                                                    onClick={(e) => {
                                                        e.preventDefault()
                                                        e.stopPropagation()
                                                        handleToggleWithClose(
                                                            cid
                                                        )
                                                    }}
                                                    className={` ${hoveredId === cid ? 'opacity-100' : 'opacity-0'}`}
                                                />
                                                {collection.isPrivate && (
                                                    <Lock
                                                        size={16}
                                                        className="shrink-0 text-gray-500"
                                                        aria-label="Private collection"
                                                    />
                                                )}
                                            </div>
                                        </div>
                                    )
                                })}
                            </div>
                        </div>
                    </div>
                )}
                {/* Create new collection flow */}
                <section className="fixed">
                    {showCreateCollectionFlow && (
                        <CreateCollectionFlow
                            open={showCreateCollectionFlow}
                            onClose={() => setShowCreateCollectionFlow(false)}
                            onCollectionCreated={() => {
                                setShowCreateCollectionFlow(false)
                                setQuery('')
                            }}
                        />
                    )}
                </section>
            </>
        )
    }
)

AddToCollectionPanel.displayName = 'AddToCollectionPanel'
